import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DjayAvatar, DjayEmotion } from '../../mascot/DjayAvatar';
import { DjayBubble } from '../../mascot/DjayBubble';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { soundManager } from '../../utils/soundManager';
import { getDjayGreeting, getDjayReturnGreeting } from '../../mascot/djayMessages';
interface FloatingDjayProps {
  currentView?: string;
}
export function FloatingDjay({
  currentView
}: FloatingDjayProps) {
  const {
    state,
    activeAccount,
    isDemoMode
  } = useAppStore();
  const {
    itemVariants,
    shouldAnimate
  } = useMotionBudget();
  const [isBubbleVisible, setIsBubbleVisible] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState('');
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Determine context
  const streak = state?.streak?.currentTotalStreak || 0;
  const displayName = activeAccount?.displayName || 'vriend';
  // Update emotion based on context
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 20 || hour < 7) {
      setEmotion('sleeping');
    } else if (streak >= 5) {
      setEmotion('excited');
    } else if (streak === 0) {
      setEmotion('encouraging');
    } else if (isIdle) {
      setEmotion('thinking');
    } else {
      setEmotion('happy');
    }
  }, [streak, isIdle, currentView]);
  // Idle detection
  useEffect(() => {
    const resetIdle = () => {
      setIsIdle(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // 5 minutes idle
      idleTimerRef.current = setTimeout(() => setIsIdle(true), 5 * 60 * 1000);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);
  const handleClick = () => {
    if (isBubbleVisible) {
      setIsBubbleVisible(false);
      return;
    }
    // Determine message based on context
    let msg = '';
    let newEmotion = emotion;
    if (isIdle) {
      msg = 'Zullen we verder gaan?';
      newEmotion = 'thinking';
    } else if (currentView === 'progress') {
      msg = 'Kijk hoe ver je al bent!';
      newEmotion = 'proud';
    } else if (currentView === 'settings') {
      msg = 'Pas alles aan zoals jij wilt!';
      newEmotion = 'happy';
    } else if (streak >= 5) {
      msg = `Wow, een streak van ${streak}! Ga zo door!`;
      newEmotion = 'celebrating';
    } else {
      const greeting = getDjayGreeting(displayName);
      msg = greeting.text;
      newEmotion = greeting.emotion;
    }
    setBubbleMessage(msg);
    setEmotion(newEmotion);
    setIsBubbleVisible(true);
    soundManager.playDjaySpeak();
  };
  // Don't show in practice view or if no active account (unless demo)
  if (currentView === 'practice' || !activeAccount && !isDemoMode) {
    return null;
  }
  return <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex items-end justify-end pointer-events-none" data-id="element-1939">
      <div className="relative flex items-end pointer-events-auto" data-id="element-1940">
        {/* Bubble */}
        <div className="absolute bottom-full right-4 mb-2 md:mb-4 origin-bottom-right" data-id="element-1941">
          <DjayBubble message={bubbleMessage} isVisible={isBubbleVisible} onDismiss={() => setIsBubbleVisible(false)} autoDismissMs={3000} position="top" data-id="element-1942" />
        </div>

        {/* Avatar */}
        <motion.button onClick={handleClick} className="relative focus:outline-none focus:ring-4 focus:ring-orange-300 rounded-full" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.95
      } : undefined} animate={shouldAnimate && !isBubbleVisible ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform'
      }} data-id="element-1943">
          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-1944" />

          <div className="w-12 h-12 md:w-16 md:h-16 relative" data-id="element-1945">
            <DjayAvatar emotion={emotion} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-1946" />
          </div>
        </motion.button>
      </div>
    </div>;
}